import MailLink from 'features/SetupTelecom/MailLink'
import { useDebounce } from 'hooks/useDebounce'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, useHistory, useParams } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import telcoApi from 'services/api/telcoSetup'

import { getOtherHotelDetails } from '../../actions/hotelDetails'
import CustomHeader from '../../components/ReusableComponents/HeaderTitles/index'
import LoadingIndicator from '../../components/ReusableComponents/LoadingIndicator'
import HotelInfoBanner from './HotelInfoBanner/index'
import styles from './HotelNumberPicker.module.scss'
import NumberList from './NumberList/index'
import PhoneNumberSearchbar from './PhoneNumberSearchbar'
import SearchRow from './SearchRow/index'
import ZipSearchRow from './ZipSearchRow/ZipSearchRow'
import { isBandwidthAddressError } from './bandwidthAddressError'

const HotelNumberPicker = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [bandwidthErrorDetail, setBandwidthErrorDetail] = useState('')
  const [searchResults, setSearchResults] = useState(null)
  const [numberList, setNumberList] = useState([])
  const [phoneSearchText, setPhoneSearchText] = useState('')
  const [hasBandwidthError, setHasBandwidthError] = useState(false)
  const [tab, setTab] = useState('phone')
  const debouncedPhoneSearchText = useDebounce(phoneSearchText, 400)
  const filteredResults = useMemo(() => {
    if (!debouncedPhoneSearchText) return searchResults

    return searchResults.filter(({ number }) => number.includes(debouncedPhoneSearchText))
  }, [searchResults, debouncedPhoneSearchText])

  const { addToast } = useToasts()

  const dispatch = useDispatch()

  const history = useHistory()

  const { id: subsidiaryId } = useParams()

  const getNumbers = async (areaCode, pattern, zipCode) => {
    setNumberList([])
    setLoading(true)

    telcoApi
      .numberSearch({ areaCode, pattern, zipCode })
      .then(res => {
        setSearchResults(res.numbers)
      })
      .catch(err => {
        addToast(err.toString(), { appearance: 'error', autoDismiss: true })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const { [subsidiaryId]: hotelDetails } = useSelector(
    s => s.hotelDetails.otherSubsidiaries
  )

  const sendNumbers = async () => {
    setLoading(true)
    setError('')
    setHasBandwidthError(false)
    setBandwidthErrorDetail('')

    telcoApi
      .buyNumber({ numbersToBuy: numberList, subsidiaryId })
      .then(() => {
        history.push('/hotelDetails?showProcessingNotification=true')
      })
      .catch(err => {
        const message = err.toString()
        if (isBandwidthAddressError(message)) {
          setHasBandwidthError(true)
          setBandwidthErrorDetail(message)
        } else {
          setError(message)
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    if (!hotelDetails) {
      dispatch(getOtherHotelDetails(subsidiaryId))
    }
  }, [dispatch, hotelDetails, subsidiaryId])

  if (!hotelDetails) {
    return <LoadingIndicator />
  }

  const numberOfPhonesToSelect = 1

  function toggleNumber(number) {
    if (numberList.includes(number))
      setNumberList(numberList.filter(item => item !== number))
    else {
      if (numberList.length < numberOfPhonesToSelect) {
        setNumberList([...numberList, number])
      }
    }
  }

  return (
    <div className={styles.page}>
      <CustomHeader
        title="Buy a proxy number"
        subtitle={hotelDetails.hotelName}
        width={1000}
      />
      <HotelInfoBanner hotel={hotelDetails} />
      <div className={styles.tabs}>
        <button
          className={tab === 'phone' ? styles.activeTab : styles.emptyTab}
          onClick={() => setTab('phone')}
        >
          Phone Number
        </button>
        <button
          className={tab === 'zip' ? styles.activeTab : styles.emptyTab}
          onClick={() => setTab('zip')}
        >
          Zip Code
        </button>
      </div>

      {hasBandwidthError && (
        <div className={styles.bandwidthErrorBar} role="alert">
          <span>
            Address rejected by Bandwidth.
            {bandwidthErrorDetail ? ` ${bandwidthErrorDetail}` : ''}{' '}
          </span>
          <MailLink
            {...hotelDetails}
            className={styles.bandwidthErrorLink}
            errorDetail={bandwidthErrorDetail}
          >
            Click here
          </MailLink>
          <span>
            {' '}
            to create an email with this hotel&apos;s data and reach Bandwidth
            support.
          </span>
        </div>
      )}

      <div className={styles.container}>
        {tab === 'phone' ? (
          <SearchRow
            loading={loading}
            handleSearch={getNumbers}
            reservationHotline={hotelDetails.reservationHotLine}
            stateCode={hotelDetails.stateCode}
          />
        ) : (
          <>
            <ZipSearchRow
              loading={loading}
              handleSearch={getNumbers}
              reservationHotline={hotelDetails.reservationHotLine}
              stateCode={hotelDetails.stateCode}
              initialZipCode={hotelDetails.postalCode}
            />
          </>
        )}

        <PhoneNumberSearchbar
          query={phoneSearchText}
          setQuery={value => setPhoneSearchText(value)}
        />

        <NumberList
          selectedNumbers={numberList}
          list={filteredResults}
          toggleNumber={number => toggleNumber(number)}
        />
      </div>
      <div className={styles.flexButtonsHolder}>
        <NavLink to="/hotelDetails">
          <button className={styles.secondaryButton}>Cancel</button>
        </NavLink>
        <button
          disabled={loading || numberList.length !== numberOfPhonesToSelect}
          onClick={sendNumbers}
          className={styles.primaryButton}
        >
          Buy & save phone number
        </button>
      </div>
      {error && <p className={styles.error}>{error}</p>}
    </div>
  )
}

export default HotelNumberPicker
